import { FC, useState } from "react";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Grade as GradeIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import {
  useGradeLevelHook,
  useDeleteGradeLevelHook,
} from "../../../hooks/useAcademicHook";
import { TGradeLevel } from "../../../apis/academic/academic.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import ManageGradeLevelDialog from "./manageGradeLevelDialog";

const GradeLevelsView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: gradeLevels, isLoading, error } = useGradeLevelHook();
  const deleteGradeLevelMutation = useDeleteGradeLevelHook();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gradeLevelToDelete, setGradeLevelToDelete] =
    useState<TGradeLevel | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [gradeLevelToEdit, setGradeLevelToEdit] = useState<TGradeLevel | null>(
    null,
  );

  const handleAddGradeLevel = () => {
    setGradeLevelToEdit(null);
    setManageDialogOpen(true);
  };

  const handleEditGradeLevel = (gradeLevel: TGradeLevel) => {
    setGradeLevelToEdit(gradeLevel);
    setManageDialogOpen(true);
  };

  const handleCloseManageDialog = () => {
    setManageDialogOpen(false);
    setGradeLevelToEdit(null);
  };

  const handleDeleteGradeLevel = (gradeLevel: TGradeLevel) => {
    setGradeLevelToDelete(gradeLevel);
    setDeleteError(null);

    // Check if this is the only grade level (minimum requirement)
    if (gradeLevels && gradeLevels.length <= 1) {
      setDeleteError(
        "Cannot delete grade level. At least one grade level is required for the institute. Please add another grade level before deleting this one.",
      );
    }

    // Note: Grade levels might be used in assessments, but we don't have access to assessment data here
    // The backend should handle this validation

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!gradeLevelToDelete) return;

    if (deleteError) {
      setDeleteDialogOpen(false);
      setGradeLevelToDelete(null);
      setDeleteError(null);
      return;
    }

    try {
      await deleteGradeLevelMutation.mutateAsync(gradeLevelToDelete.id);
      logger.show_success("Grade level deleted successfully!");
      setDeleteDialogOpen(false);
      setGradeLevelToDelete(null);
      setDeleteError(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete grade level. Please try again.";
      // Check if error indicates dependency issue
      if (
        errorMessage.toLowerCase().includes("dependency") ||
        errorMessage.toLowerCase().includes("in use")
      ) {
        setDeleteError(errorMessage);
      } else {
        logger.show_error(errorMessage);
      }
    }
  };

  const sortedGradeLevels = gradeLevels
    ? [...gradeLevels].sort((a, b) => {
      const aMax = a.maxPercentage ?? a.maxScore ?? 0;
      const bMax = b.maxPercentage ?? b.maxScore ?? 0;
      return bMax - aMax;
    })
    : [];

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Scoring rubrics`} />
        <Box p={3} data-testid="grade-levels-view">
          <MainTitle title="Scoring rubrics" />
          <Alert severity="info" data-testid="grade-levels-no-institute">
            Please select an institute from the dashboard to manage scoring rubrics.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Scoring rubrics`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="grade-levels-view"
        >
          <CircularProgress data-testid="grade-levels-loading" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Scoring rubrics`} />
        <Box p={3} data-testid="grade-levels-view">
          <MainTitle title="Scoring rubrics" />
          <Alert severity="error" data-testid="grade-levels-error">
            Failed to load scoring rubrics. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Scoring rubrics`} />

      <Grid container spacing={3} data-testid="grade-levels-view">
        <Grid item xs={12}>
          <Box
            data-testid="grade-levels-header"
            sx={{ display: 'flex', flexDirection: 'column', flexGrow: 0 }}
          >
            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <MainTitle title="Scoring rubrics" />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddGradeLevel}
                size="large"
                data-testid="add-grade-level-button"
              >
                Add rubric
              </Button>
            </Box>
            <Alert severity="info" data-testid="grade-levels-no-institute">
              Scoring rubrics define bands for grading (for example A/B/C or percentage ranges). They are stored via the admin scoring-rubrics API.
            </Alert>
          </Box>
        </Grid>

        {sortedGradeLevels.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="grade-levels-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="grade-levels-empty-content"
                >
                  <GradeIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    No grade levels configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get started by adding your first grade level.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddGradeLevel}
                    data-testid="add-grade-level-empty-button"
                  >
                    Add Grade Level
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              data-testid="grade-levels-table-container"
            >
              <Table data-testid="grade-levels-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Grade Name</TableCell>
                    <TableCell>Range</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedGradeLevels.map((gradeLevel) => (
                    <TableRow
                      key={gradeLevel.id}
                      hover
                      data-testid={`grade-level-row-${gradeLevel.id}`}
                    >
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {gradeLevel.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {gradeLevel.minPercentage !== undefined &&
                          gradeLevel.maxPercentage !== undefined ? (
                          <Chip
                            label={`${gradeLevel.minPercentage}% - ${gradeLevel.maxPercentage}%`}
                            variant="outlined"
                            size="small"
                            data-testid={`grade-level-range-${gradeLevel.id}`}
                          />
                        ) : gradeLevel.minScore !== undefined &&
                          gradeLevel.maxScore !== undefined ? (
                          <Chip
                            label={`${gradeLevel.minScore} - ${gradeLevel.maxScore}`}
                            variant="outlined"
                            size="small"
                            data-testid={`grade-level-range-${gradeLevel.id}`}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {gradeLevel.description || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditGradeLevel(gradeLevel)}
                          color="primary"
                          data-testid={`edit-grade-level-${gradeLevel.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteGradeLevel(gradeLevel)}
                          color="error"
                          data-testid={`delete-grade-level-${gradeLevel.id}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      <ManageGradeLevelDialog
        open={manageDialogOpen}
        onClose={handleCloseManageDialog}
        gradeLevel={gradeLevelToEdit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setGradeLevelToDelete(null);
          setDeleteError(null);
        }}
        data-testid="delete-grade-level-dialog"
      >
        <DialogTitle data-testid="delete-grade-level-dialog-title">
          Delete Grade Level
        </DialogTitle>
        <DialogContent data-testid="delete-grade-level-dialog-content">
          {deleteError ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="delete-grade-level-error"
            >
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to delete grade level "
            {gradeLevelToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-grade-level-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setGradeLevelToDelete(null);
            }}
            disabled={deleteGradeLevelMutation.isPending}
            data-testid="cancel-delete-grade-level-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteGradeLevelMutation.isPending || !!deleteError}
            startIcon={
              deleteGradeLevelMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-grade-level-button"
          >
            {deleteGradeLevelMutation.isPending
              ? "Deleting..."
              : deleteError
                ? "Close"
                : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GradeLevelsView;
